import styled, { css } from 'styled-components';

import {
    CommonAction,
    designColor,
    floorGameDuration as duration,
    floorGameEasing as easing,
    recipesCommonAction,
    styledMedia,
    styledTypography,
} from '@pkg/primitives';

import { tokens } from './FieldToggle.tokens';

interface StyledFieldToggleProps {
    $backdropBlur?: boolean;
    $border?: boolean;
    $disabled?: boolean;
}

export const StyledFieldToggle = styled.div<StyledFieldToggleProps>`
    --field-toggle-option-size: ${tokens.optionSize}px;

    display: grid;
    grid-auto-flow: column;
    align-items: center;
    border-radius: 999px;
    transition: opacity ${duration.normal}ms ${easing.cubic};

    ${({ $backdropBlur }) =>
        $backdropBlur &&
        css`
            --field-toggle-blur: blur(4px);
        `};

    ${({ $border }) =>
        $border &&
        css`
            --field-toggle-option-size: ${tokens.optionSize - tokens.borderSize * 2}px;
            padding: ${tokens.borderSize * 2}px;
            border: ${tokens.borderSize}px solid rgb(255, 255, 255, 0.1);
        `};

    ${({ $disabled }) =>
        $disabled &&
        css`
            opacity: 0.8;
        `};
`;

export const Label = styled.p`
    ${styledTypography.allianceBase};
    ${styledTypography.textBoxTrim};
    padding-left: ${tokens.labelSize}px;
    padding-right: ${tokens.labelSize}px;
    font-size: ${tokens.labelSize}px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: rgb(255, 255, 255, 0.8);
`;

export const Action = styled(CommonAction)`
    ${recipesCommonAction.basic};
    position: relative;
    background-color: rgb(255, 255, 255, 0.1);
    backdrop-filter: var(--field-toggle-blur);

    // Circular highlight
    &::before {
        content: '';
        display: block;
        position: absolute;
        z-index: 1;
        top: 0;
        left: 0;
        width: var(--field-toggle-option-size);
        height: var(--field-toggle-option-size);
        border-radius: 999px;
        background-color: ${designColor.brandPurple};
        transform-origin: center left;
        transition-property: background-color, scale, translate, transform-origin;
        transition-duration: ${duration.normal}ms;
        transition-timing-function: ${easing.cubic};
    }

    > div:last-child {
        color: rgb(255, 255, 255, 0.6);
    }

    &:disabled::before {
        background-color: rgb(255, 255, 255, 0.1);
    }

    &[aria-pressed] {
        > div:first-child {
            color: rgb(255, 255, 255, 0.6);
        }

        > div:last-child {
            color: rgb(255, 255, 255, 1);
        }

        &::before {
            translate: var(--field-toggle-option-size);
            transform-origin: center right;
        }
    }

    &:hover {
        background-color: rgb(255, 255, 255, 0.16);
    }

    &:active {
        background-color: rgb(255, 255, 255, 0.1);

        &::before {
            scale: 1.1 1;
        }
    }
`;

export const Option = styled.div`
    position: relative;
    z-index: 2;
    display: grid;
    place-items: center;
    place-content: center;
    width: var(--field-toggle-option-size);
    height: var(--field-toggle-option-size);
    transition: color ${duration.normal}ms ${easing.cubic};
`;

export const IconWrapper = styled.div`
    ${styledMedia.iconWrapper};
    width: ${tokens.iconSize}px;
    height: ${tokens.iconSize}px;
`;
